import { toast } from 'sonner'
import { axiosInstance } from '@/@types/generated/core/request'
import { parseErrorBlob } from '../siteFunctions'

const interceptor = () => {
  axiosInstance.interceptors.request.use(
    (config: any) => {
      config.headers = {
        ...config.headers,
        'Cache-Control': 'no-cache,no-redux,must-revalidate,max-age=0,private',
      } as any

      if (process.env.NODE_ENV !== 'production') {
        config.headers = {
          ...config.headers,
        } as any
      }

      return config
    },
    (error: any) => Promise.reject(error)
  )

  axiosInstance.interceptors.response.use(
    (response: any) => {
      return response
    },
    async (error: any) => {
      const { message, code, requestId } = await parseErrorBlob(error)

      // Handle specific error codes with user-friendly messages
      switch (code) {
        case 400:
          // Bad Request - let components handle validation errors
          break
        case 401:
          // Unauthorized - handled by auth context
          break
        case 403:
          toast.error('Access Denied', {
            description: 'You do not have permission to perform this action.',
            duration: 5000,
          })
          break
        case 404:
          // Not Found - let components handle not found errors
          break
        case 500:
          toast.error('Server Error', {
            description:
              message ??
              'An internal server error occurred. Please try again later.',
            duration: 6000,
          })
          break
        case 503:
          toast.error('Service Unavailable', {
            description:
              'The service is temporarily unavailable. Please try again later.',
            duration: 6000,
          })
          break
        case 504:
          toast.error('Request Timeout', {
            description:
              'The request took too long to complete. Please try again.',
            duration: 5000,
          })
          break
        case 'ERR_NETWORK':
          toast.error('Network Error', {
            description:
              'Unable to connect to the server. Please check your internet connection.',
            duration: 6000,
          })
          break
        default:
          // Only show generic errors for 5xx status codes, not for validation errors
          if (typeof code === 'number' && code >= 500) {
            toast.error('Unexpected Error', {
              description:
                'An unexpected error occurred. Please try again later.',
              duration: 5000,
            })
          }
          // Log errors that aren't "No current user" for debugging
          if (message !== 'No current user') {
            console.error('API Error:', { message, code, requestId })
          }
      }

      // Some endpoints return the error body as a bare JSON string rather than an object
      // (e.g. UnprocessableEntity(result.Error.Message)) — spreading a string here would
      // destructure it into character-indexed keys ({0: 'P', 1: 'r', ...}) instead of merging
      // fields into it, so only spread when the original body is actually an object.
      const originalData = error?.response?.data
      const mergedData =
        originalData && typeof originalData === 'object'
          ? { ...originalData, message, requestId }
          : { message, requestId }

      return Promise.reject({
        ...error,
        response: {
          ...error?.response,
          data: mergedData,
        },
      })
    }
  )
}

export default { interceptor }
